import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  //Injector,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { ChangeDetectorRef } from "@angular/core";
import {
  AnalyticsType,
  ChartDefinition,
  ChartDefinitions,
} from "app/analytics/chartDefinitions.config";
import {
  AnalyticsService,
  AuthorizationService,
  DashboardData,
  ExportOption,
  ExportService,
  ExportType,
  TimePeriod,
  TimePeriodUtils,
} from "app/shared";
import { UserAnalyticConfiguration } from "app/shared/model/charts/user-analytic-configurations.model";
import { Subscription } from "rxjs";

@Component({
  selector: "custom-dashboard-page",
  templateUrl: "./custom-dashboard-page.html",
  styleUrls: ["./custom-dashboard-page.css"],
  standalone: false,
})
export class CustomDashboardPage implements OnDestroy {
  dashboardData: DashboardData = new DashboardData();
  isPopupOpen = false;
  isExportPopupOpen = false;
  isLoadingConfig = true;
  popupTop = 0;
  popupLeft = 0;

  chartTypes = ChartDefinitions;
  chartTypesArray: { key: string; section: ChartDefinition }[] = [];
  filteredAndSortedChartTypes: {
    type: AnalyticsType;
    charts: { key: string; section: ChartDefinition }[];
  }[] = [];
  exportOptions: ExportOption[] = [];
  exporting: boolean = false;
  exportAllTypes: ExportType[] = [ExportType.PDF, ExportType.Excel];
  shouldEnableExportAllToPDF: boolean = false;
  chartSelectedForExport: ChartDefinition;
  chartOptionsForExport: ExportType[];
  //dynamicInjector: Injector;

  currentRowIndex: number = 0;
  currentTileIndex: number = 0;

  bsConfig = {
    containerClass: "theme-dark-blue",
  };
  focusedTile: { rowIndex: number; tileIndex: number } | null = null;

  private subscriptions: Subscription[] = [];

  @ViewChild("hoverPopup") hoverPopup: ElementRef;
  @ViewChild("dashboardDesigner") dashboardDesigner: ElementRef;
  @ViewChild("hoverPopupForExport") hoverPopupForExport: ElementRef;

  timePeriods: { value: TimePeriod | string; display: string }[] = [
    {
      value: null,
      display: "Use Chart Settings",
    },
    ...Object.values(TimePeriod)
      .filter((value) => {
        // Exclude time periods based on your criteria (e.g., by hours)
        const excludedPeriods = [
          TimePeriod.OneHour,
          TimePeriod.TwoHours,
          TimePeriod.TwentyFourHours,
        ];
        return !excludedPeriods.includes(value as TimePeriod);
      })
      .map((value) => ({
        value,
        display: TimePeriodUtils.getStringForTimePeriod(value as TimePeriod),
      })),
  ];

  selectedEndDate: Date | null = null;
  selectedStartDate: Date | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private analyticsService: AnalyticsService,
    private exportService: ExportService,
    private authService: AuthorizationService, //private injector: Injector
    private _ngZone: NgZone
  ) {
    // Add click listener to detect outside clicks
    this.renderer.listen("window", "click", (event: Event) => {
      const targetElement = event.target as HTMLElement;

      // Check if the click is outside the popup and the button
      if (!this.hoverPopup.nativeElement.contains(targetElement)) {
        this.isPopupOpen = false;
        this.hoverPopup.nativeElement.style.display = "none";
      }

      if (!this.hoverPopupForExport.nativeElement.contains(targetElement)) {
        this.isExportPopupOpen = false;
        this.hoverPopupForExport.nativeElement.style.display = "none";
      }
    });

    this.subscriptions.push(
      this.analyticsService.userAnalyticsConfigurationsDataChange.subscribe(
        (data) => {
          this.isLoadingConfig = false;
          if (data && Array.isArray(data)) {
            // Find the dashboard configuration
            const dashboardConfig = data.find(
              (config) => config.configType === "dashboard"
            );
            if (dashboardConfig) {
              this.dashboardData = DashboardData.fromJSON(
                dashboardConfig.config
              );
            } else {
              this.initializeDashboardData();
            }
          } else {
            this.initializeDashboardData();
          }
          this.updateChartTypes();
        }
      )
    );

    this.analyticsService.getUserAnalyticsConfigurationsData(
      authService.userId
    );

    this.subscriptions.push(
      this.exportService.exportOptionsUpdateEvent.subscribe((data) => {
        if (data) {
          this.exportOptions = data;
          this.shouldEnableExportAllToPDF = data.some((data) =>
            data.types.includes(ExportType.PDF)
          );
        }
      })
    );

    this.subscriptions.push(
      this.exportService.exportCompleteEvent.subscribe((data) => {
        this._ngZone.run(() => {
          this.exporting = false;
        });
      })
    );
  }

  private _selectedTimePeriod: string | TimePeriod | null = null;
  get selectedTimePeriod(): TimePeriod | null {
    if (typeof this._selectedTimePeriod === "string")
      return this._selectedTimePeriod as TimePeriod;

    if (this._selectedTimePeriod === null) return null;

    return this._selectedTimePeriod as TimePeriod;
  }

  set selectedTimePeriod(value: string | TimePeriod | null) {
    this._selectedTimePeriod = value;

    if (value && value !== "Custom" && value !== "null") {
      // Adjust start and end dates based on the selected time period
      this.selectedEndDate = new Date(); // End date is always today
      this.selectedStartDate = TimePeriodUtils.subtractTimePeriodFromDate(
        value as TimePeriod,
        this.selectedEndDate
      );
    }

    // If "Custom" is selected, dates remain unchanged
    if (value === "Custom") {
      return;
    }

    if (!value || value === "null") {
      this.selectedStartDate = null;
      this.selectedEndDate = null;
    }

    this.updateCustomOption();
  }

  onDateChange() {
    if (!this.selectedStartDate || !this.selectedEndDate) {
      return;
    }

    const isToday =
      this.selectedEndDate.toDateString() === new Date().toDateString();

    if (isToday) {
      // Check if dates match any predefined time period
      const matchingPeriod = this.timePeriods.find((period) => {
        if (!period.value) return false; // Skip "Use Chart Settings"
        const calculatedStartDate = TimePeriodUtils.subtractTimePeriodFromDate(
          period.value as TimePeriod,
          this.selectedEndDate
        );
        return (
          calculatedStartDate.toDateString() ===
          this.selectedStartDate.toDateString()
        );
      });

      if (matchingPeriod) {
        this._selectedTimePeriod = matchingPeriod.value;
      } else {
        this.setCustomTimePeriod();
      }
    } else {
      this.setCustomTimePeriod();
    }
    //this.updateDynamicInjector();
  }

  private setCustomTimePeriod() {
    this._selectedTimePeriod = "Custom";

    if (!this.timePeriods.find((period) => period.value === "Custom")) {
      this.timePeriods.push({
        value: "Custom",
        display: "Custom",
      });
    }
  }

  private updateCustomOption() {
    // Remove "Custom" if it's not needed
    const customIndex = this.timePeriods.findIndex(
      (period) => period.value === "Custom"
    );
    if (customIndex !== -1) {
      this.timePeriods.splice(customIndex, 1);
    }
  }

  initializeDashboardData() {
    const newDashboardData = new DashboardData();
    const newConfig = new UserAnalyticConfiguration({
      userid: this.authService.userId,
      configType: "dashboard",
      config: newDashboardData,
    });
    this.dashboardData = newDashboardData;
    this.analyticsService.setUserAnalyticsConfigurationsData(newConfig);
  }

  private loadChartTypes(type: AnalyticsType): void {
    this.filteredAndSortedChartTypes.push({
      type,
      charts: [
        ...this.chartTypesArray
          .filter(
            (entry) =>
              entry.section.type === type &&
              this.dashboardData.getActiveCharts().indexOf(entry.key) === -1
          )
          .sort((a, b) => {
            // Sort by sortOrder, then alphabetically by title
            if (a.section.sortOrder === b.section.sortOrder) {
              return a.section.title.localeCompare(b.section.title);
            }
            return a.section.sortOrder - b.section.sortOrder;
          }),
      ],
    });
  }

  updateChartTypes() {
    this.chartTypesArray = Object.keys(this.chartTypes).map((key) => ({
      key,
      section: this.chartTypes[key],
    }));
    this.filteredAndSortedChartTypes = [];
    this.loadChartTypes(AnalyticsType.App);
    this.loadChartTypes(AnalyticsType.User);
    this.loadChartTypes(AnalyticsType.Manuals);
  }

  private saveDashboardData() {
    const userAnalyticsConfig =
      this.analyticsService.userAnalyticsConfigurationsData.find(
        (config) => config.configType === "dashboard"
      );
    if (userAnalyticsConfig) {
      userAnalyticsConfig.config = this.dashboardData;
      this.analyticsService.setUserAnalyticsConfigurationsData(
        userAnalyticsConfig
      );
    } else {
      const newConfig = new UserAnalyticConfiguration({
        userid: this.authService.userId,
        configType: "dashboard",
        config: this.dashboardData,
      });
      this.analyticsService.userAnalyticsConfigurationsData.push(newConfig);
      this.analyticsService.setUserAnalyticsConfigurationsData(newConfig);
    }
  }

  addRow() {
    this.dashboardData.addRow();

    let scrollHeight = this.dashboardDesigner.nativeElement.scrollHeight;
    let scrollTop = this.dashboardDesigner.nativeElement.scrollTop;
    this.dashboardDesigner.nativeElement.scrollTop = Math.max(
      0,
      scrollHeight - scrollTop
    );

    this.saveDashboardData();
  }

  openChartDialog(event: MouseEvent, tileIndex: number, rowIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    const targetElement = event.target as HTMLElement;
    const buttonRect = targetElement.getBoundingClientRect(); // Get the bounding rectangle of the clicked element

    this.popupTop = buttonRect.bottom;
    this.popupLeft = buttonRect.left;
    const popupWidth = this.hoverPopup.nativeElement.offsetWidth;
    const popupHeight = this.hoverPopup.nativeElement.offsetHeight;

    if (this.popupLeft + popupWidth > window.innerWidth) {
      this.popupLeft = buttonRect.right - popupWidth;
    }
    if (this.popupTop + popupHeight > window.innerHeight) {
      this.popupTop = buttonRect.top - popupHeight;
    }

    this.currentRowIndex = rowIndex;
    this.currentTileIndex = tileIndex;
    this.isPopupOpen = true;
    this.isExportPopupOpen = false;
    this.hoverPopup.nativeElement.style.display = "block";
    this.hoverPopupForExport.nativeElement.style.display = "none";
  }

  openExportDialog(event: MouseEvent, tileIndex: number, rowIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    const targetElement = event.target as HTMLElement;
    const buttonRect = targetElement.getBoundingClientRect(); // Get the bounding rectangle of the clicked element

    this.popupTop = buttonRect.bottom;
    this.popupLeft = buttonRect.left;
    const popupWidth = this.hoverPopup.nativeElement.offsetWidth;
    const popupHeight = this.hoverPopup.nativeElement.offsetHeight;

    if (this.popupLeft + popupWidth > window.innerWidth) {
      this.popupLeft = buttonRect.right - popupWidth;
    }
    if (this.popupTop + popupHeight > window.innerHeight) {
      this.popupTop = buttonRect.top - popupHeight;
    }

    this.currentRowIndex = rowIndex;
    this.currentTileIndex = tileIndex;
    this.isPopupOpen = false;
    this.isExportPopupOpen = true;
    this.hoverPopup.nativeElement.style.display = "none";
    this.hoverPopupForExport.nativeElement.style.display = "block";

    const chartId = this.dashboardData.rows[rowIndex].tiles[tileIndex].chart;
    const chartDefinitionForExport = this.chartTypes[chartId];
    const chartTitle = chartDefinitionForExport.title;
    this.chartSelectedForExport = chartDefinitionForExport;
    this.chartOptionsForExport = this.exportOptions.find(
      (eo) => eo.chart === chartTitle
    )?.types;
  }

  //   private updateDynamicInjector() {
  //     this.dynamicInjector = Injector.create({
  //       providers: [
  //         { provide: "startDate", useValue: this.selectedStartDate },
  //         { provide: "endDate", useValue: this.selectedEndDate },
  //       ],
  //       parent: this.injector,
  //     });
  //   }

  onSelectChart(event: Event, chartKey: string): void {
    event.preventDefault();

    this.dashboardData.setTileChart(
      this.currentRowIndex,
      this.currentTileIndex,
      chartKey
    );
    this.updateChartTypes();
    //this.updateDynamicInjector();
    this.isPopupOpen = false; // Close the popup after selection
    this.hoverPopup.nativeElement.style.display = "none";
    this.saveDashboardData();
  }

  onSelectChartForExport(
    event: Event,
    chartTitle: string,
    exportOption: ExportType
  ): void {
    event.preventDefault();

    this.isExportPopupOpen = false;
    this.hoverPopupForExport.nativeElement.style.display = "none";

    this.exportService.exportButtonClick(chartTitle, exportOption);
  }

  refresh() {
    this.analyticsService.refreshData.emit();
  }

  deleteRow(event: Event, rowIndex: number) {
    this.dashboardData.removeRow(rowIndex);
    this.updateChartTypes();
    this.saveDashboardData();
  }

  deleteTile(event: Event, rowIndex: number, tileIndex: number) {
    this.dashboardData.removeTile(rowIndex, tileIndex);
    this.updateChartTypes();
    this.saveDashboardData();
  }

  onExportAllClick(event: Event, exportOption: ExportType) {
    event.preventDefault();
    this._ngZone.run(() => {
      this.exporting = true;
    });

    this.exportService.exportAll(this.exportOptions, exportOption);
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  focusChart(rowIndex: number, tileIndex: number) {
    this.focusedTile = { rowIndex, tileIndex };
  }

  // Optionally, add a method to unfocus (e.g., on ESC or clicking outside)
  unfocusChart() {
    this.focusedTile = null;
  }
}
